/* CASH REPORT — THE MONTH-END RECONCILE (2026-10-08).

   Tuan's "Month-end Recconcile": per entity and month, every account's closing on the MONTHLY bank
   statement against the closing the weekly report gave for the week-end — the last Friday on or
   before the month end — and the money that moved between the two dates. Reconcile = Variance −
   Inflow + Outflow and has to come to 0 (Tuan, 2026-10-13). Which week that is, is printed above the
   table, because it is the one thing on this screen that changes from month to month.

   The month list is every month the Set up weeks cover; a month opens for its reconcile once it has
   ended. No entity panel under the table (Max, 2026-10-15: "phần này không cần hiện trong các report
   nhé") — an entity's own reconcile is read and decided in Cash Report Approval. */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { MonthEndHead } from './CashForms';
import { API, call, fmtDay, fmtStamp, moneyIn, Num, StateChip, PeriodBar, TestAsBar, WaitingTag } from './cashUi';
import { MonthEndCharts } from './CashCharts';
import './SPHub.css';
import './ProjectApproval.css';
import './CashSetup.css';
import './CashWeekly.css';

// The stamps of the flow, as Tuan's status columns — Prepared · Reviewed. The Group CFO's and the CEO's
// steps came out on 2026-10-16 (Max: "CFO và CEO thì ko tham gia vào quá trình duyệt nhé").
const STAMPS = ['submitted', 'reviewed'];

export default function CashMonthEnd() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const period = params.get('period') || '';

  const load = useCallback(async () => {
    try {
      setData(await call(`${API}/view/monthend${period ? `?period=${encodeURIComponent(period)}` : ''}`)); setError('');
    } catch (e) { setError(e.message); }
  }, [period]);
  useEffect(() => { load(); }, [load]);
  const setQuery = (next) => {
    const p = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => { if (v) p.set(k, v); else p.delete(k); });
    setParams(p, { replace: true });
  };

  if (error) return <div className="pa-banner err">{error}</div>;
  if (!data) return <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('cash.loading')}</div>;
  const m = data.period;
  return (
    <>
        <TestAsBar t={t} me={data.me} />

        <PeriodBar t={t} periods={data.periods} period={m} onPick={(code) => setQuery({ period: code })}>
          {t('cashm.monthLine', { month: m.label, end: fmtDay(m.end) })}
          {data.rule && <div className="cw-sub cw-rule"><b>{t('cashm.ruleHead', { week: data.rule.week, end: fmtDay(data.rule.weekEnd) })}</b> {data.rule.text}</div>}
        </PeriodBar>

        {/* THE REPORT, in Tuan's "Month-end Recconcile" format: each entity, its accounts under it,
            and the report's status — who prepared and reviewed it, and when. */}
        <MonthEndCharts t={t} data={data} />
        <div className="pa-section cw-monthend">
          <div className="cs-table-wrap">
            <table className="pa-table cw-table">
              {/* Tuan's report heads: "Net-off" over its Inflow and Outflow, "Status" over the steps. */}
              <MonthEndHead t={t} week={data.rule?.week || ''}
                tail={<><th rowSpan={2}>{t('cashm.h_reconcile')}</th><th colSpan={STAMPS.length} className="t3-netoff">{t('cashm.statusGroup')}</th></>}
                tail2={STAMPS.map((k) => <th key={k}>{t(`cashm.st_${k}`)}</th>)} />
              <tbody>
                {data.entities.map((e) => (
                  <React.Fragment key={e.entity}>
                    <tr className="cw-entrow cw-enthdr">
                      <td colSpan={2}>
                        <span className="cw-entlink" title={e.name}>{e.entity}</span>
                        <WaitingTag t={t} data={data} entity={e.entity} />
                        {e.errors > 0 && <span className="cs-tab-err" title={t('cashw.errorsN', { count: e.errors })}>{e.errors}</span>}
                        <div className="cw-sub">{e.name}</div>
                      </td>
                      {e.totals ? (
                        <>
                          <td className="num"><Num v={e.totals.month_end_sgd} ccy="SGD" />{e.totals.unconverted > 0 && <div className="cw-sub">{t('cashm.unconverted', { n: e.totals.unconverted })}</div>}</td>
                          <td colSpan={4} />
                          <td>{e.totals.reconciled === e.totals.accounts
                            ? <span className="cw-up">{t('cashm.allReconciled', { n: e.totals.accounts })}</span>
                            : <span className="cw-down">{e.totals.reconciled} / {e.totals.accounts}</span>}</td>
                        </>
                      ) : <td colSpan={6} className="pa-muted"><StateChip t={t} row={e} />{e.pending && <span className="cw-sub"> {t('cashw.notReviewed')}</span>}</td>}
                      {STAMPS.map((k) => {
                        const [by, at] = e.stamps?.[k] || [];
                        return <td key={k} className="cw-sub">{by ? <>{by}<br />{fmtStamp(at)}</> : ''}</td>;
                      })}
                    </tr>
                    {e.lines.map((l) => (
                      <tr key={l.account_id} className="cw-subrow">
                        <td className="cw-indent"><span className="mono">{l.account_id}</span></td><td>{l.currency}</td>
                        <td className="num"><Num v={l.month_end_closing} ccy={l.currency} /></td><td className="num"><Num v={l.weekend_closing} ccy={l.currency} /></td>
                        <td className="num"><Num v={l.variance} ccy={l.currency} /></td><td className="num"><Num v={l.net_inflow} ccy={l.currency} /></td><td className="num"><Num v={l.net_outflow} ccy={l.currency} /></td>
                        <td>{l.reconcile === 0 ? <CheckCircle2 size={13} className="cw-ok" /> : (l.reconcile != null ? <span className="cw-down">{moneyIn(l.reconcile, l.currency)}</span> : '')}</td>
                        <td colSpan={STAMPS.length} />
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="cs-hint">{t('cashm.reportNote')}</p>
        </div>

    </>
  );
}
